import { Badge, Box } from "@mui/material"
import {
  DatePicker,
  LocalizationProvider,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/es"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { AddEventoModalProps, IEventos } from "../../interfaces/IEventos"
import { TipoEvento } from "../../interfaces/TipoEvento"
import { TIPO_EVENTO_THEME, getThemeByTipo } from "../../utils/tipoEventoTheme"
import "./AddEventoModal.css"

const AddEventoModal: React.FC<AddEventoModalProps> = ({
  show,
  handleClose,
  handleEventoAdd,
  eventos = [],
  fechaDefecto,
  tipoDefecto,
}) => {
  const [nombre, setNombre] = useState("")
  const [fecha, setFecha] = useState<Dayjs | null>(null)
  const [tipo, setTipo] = useState<TipoEvento | "">(tipoDefecto ?? "")
  const [nombreCliente, setNombreCliente] = useState("")
  const [apellidoCliente, setApellidoCliente] = useState("")
  const [dniCliente, setDniCliente] = useState("")
  const [telefono, setTelefono] = useState("")
  const [isTipoValid, setIsTipoValid] = useState(true)

  useEffect(() => {
    if (show) {
      setTipo(tipoDefecto ?? "")
      setIsTipoValid(true)
      if (fechaDefecto) setFecha(dayjs(fechaDefecto))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [isFechaValid, setIsFechaValid] = useState(true)

  // Mapa de eventos por fecha para el calendario
  const eventosPorFecha = useMemo(() => {
    const mapa: Record<string, IEventos[]> = {}
    eventos.forEach((evento) => {
      const fechaKey = dayjs(evento.fecha).format("YYYY-MM-DD")
      if (!mapa[fechaKey]) mapa[fechaKey] = []
      mapa[fechaKey].push(evento)
    })
    return mapa
  }, [eventos])

  // Componente personalizado para los días del calendario con puntos de color por tipo
  const DiaConBadge = (props: PickersDayProps<Dayjs>) => {
    const { day, outsideCurrentMonth, ...other } = props
    const fechaKey = day.format("YYYY-MM-DD")
    const eventosEnFecha = outsideCurrentMonth
      ? []
      : eventosPorFecha[fechaKey] || []
    const tiposUnicos = [
      ...new Set(eventosEnFecha.map((e) => e.tipo ?? TipoEvento.EGRESADOS)),
    ]
    const hasEvents = !outsideCurrentMonth && tiposUnicos.length > 0

    const badgeContent = hasEvents ? (
      tiposUnicos.length === 1 ? (
        eventosEnFecha.length
      ) : (
        <Box sx={{ display: "flex", gap: "2px", alignItems: "center" }}>
          {tiposUnicos.map((tipo) => (
            <Box
              key={tipo}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: getThemeByTipo(tipo).border,
              }}
            />
          ))}
        </Box>
      )
    ) : undefined

    const badgeColor =
      hasEvents && tiposUnicos.length === 1
        ? getThemeByTipo(tiposUnicos[0]).border
        : "transparent"

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={badgeContent}
        sx={{
          "& .MuiBadge-badge": {
            fontSize: "0.6rem",
            height: "16px",
            minWidth: "16px",
            backgroundColor: badgeColor,
            color: "#fff",
            padding: tiposUnicos.length > 1 ? 0 : undefined,
          },
        }}
      >
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
        />
      </Badge>
    )
  }

  const resetForm = () => {
    setNombre("")
    setFecha(null)
    setTipo(tipoDefecto ?? "")
    setNombreCliente("")
    setApellidoCliente("")
    setDniCliente("")
    setTelefono("")
    setIsSubmitting(false)
    setIsFechaValid(true)
    setIsTipoValid(true)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!tipo) {
      setIsTipoValid(false)
      return
    }
    if (!isFechaValid || !fecha) {
      setIsFechaValid(false)
      return
    }

    setIsSubmitting(true)

    const nuevoEvento: IEventos = {
      id: Date.now(),
      nombre,
      fecha: fecha.format("YYYY-MM-DD"),
      tipo: tipo as TipoEvento,
      nombreCliente:
        tipo === TipoEvento.CUMPLEANIOS_BODAS ? nombreCliente.trim() : "",
      apellidoCliente:
        tipo === TipoEvento.CUMPLEANIOS_BODAS ? apellidoCliente.trim() : "",
      dniCliente: tipo === TipoEvento.CUMPLEANIOS_BODAS ? dniCliente.trim() : "",
      telefono: tipo === TipoEvento.CUMPLEANIOS_BODAS ? telefono.trim() : "",
      listaEgresados: [],
    }

    try {
      handleEventoAdd(nuevoEvento)
      handleClose()
      resetForm()
    } catch (error) {
      console.error("Error al agregar evento:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseModal = () => {
    resetForm()
    handleClose()
  }

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto"
      textAreaRef.current.style.height = `${Math.max(
        textAreaRef.current.scrollHeight,
        50,
      )}px`
    }
  }, [nombre])

  const handleFechaChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setFecha(newValue)
      setIsFechaValid(true) // Actualiza a válido si hay una fecha seleccionada
    } else {
      setFecha(null)
      setIsFechaValid(false) // Marca como inválido si no hay fecha
    }
  }


  return (
    <Modal show={show} onHide={handleCloseModal}>
      <Modal.Header closeButton>
        <Modal.Title>Agregar evento</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md="6">
              <Form.Group className="mb-3" controlId="formNombre">
                <Form.Label>Nombre Evento</Form.Label>
                <Form.Control
                  as="textarea"
                  placeholder="Ingrese nombre"
                  value={nombre}
                  onChange={(event) => setNombre(event.target.value)}
                  required
                  ref={textAreaRef}
                  className="form-control-lg custom-placeholder-add"
                  style={{ resize: "none" }}
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="formTipo">
                <Form.Label>Tipo de evento</Form.Label>
                <Form.Select
                  value={tipo}
                  onChange={(e) => {
                    const nuevoTipo = e.target.value as TipoEvento
                    setTipo(nuevoTipo)
                    setIsTipoValid(true)
                    if (nuevoTipo !== TipoEvento.CUMPLEANIOS_BODAS) {
                      setNombreCliente("")
                      setApellidoCliente("")
                      setDniCliente("")
                      setTelefono("")
                    }
                  }}
                  style={{
                    borderColor: tipo
                      ? TIPO_EVENTO_THEME[tipo as TipoEvento].border
                      : "#dc3545",
                  }}
                  isInvalid={!isTipoValid}
                >
                  <option value="" disabled>
                    Seleccionar tipo...
                  </option>
                  {Object.values(TipoEvento).map((t) => (
                    <option key={t} value={t}>
                      {TIPO_EVENTO_THEME[t].descripcion}
                    </option>
                  ))}
                </Form.Select>
                {!isTipoValid && (
                  <Form.Control.Feedback type="invalid">
                    Debe seleccionar un tipo de evento
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group className="mb-3" controlId="formFecha">
                <Form.Label>Fecha</Form.Label>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es"
                >
                  <DatePicker
                    label="Seleccionar Fecha"
                    value={fecha ? dayjs(fecha) : null}
                    onChange={handleFechaChange}
                    format="DD/MM/YYYY"
                    className="date-picker form-control-lg w-100"
                    slots={{
                      day: DiaConBadge,
                    }}
                    onError={(error) => {
                      setIsFechaValid(!error)
                    }}
                  />
                </LocalizationProvider>
                {!isFechaValid && (
                  <Form.Text className="text-danger">
                    Este campo es obligatorio y debe ser una fecha válida
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
          {tipo === TipoEvento.CUMPLEANIOS_BODAS && (
            <>
              <div className="d-flex align-items-center mt-3 mb-2">
                <div className="flex-grow-1 border-top"></div>
                <span className="mx-3 text-muted fw-semibold small">
                  Datos del Cliente
                </span>
                <div className="flex-grow-1 border-top"></div>
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="formNombreCliente">
                    <Form.Label>Nombre</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ingrese nombre"
                      value={nombreCliente}
                      onChange={(event) => setNombreCliente(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="formApellidoCliente">
                    <Form.Label>Apellido</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ingrese apellido"
                      value={apellidoCliente}
                      onChange={(event) => setApellidoCliente(event.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="formDniCliente">
                    <Form.Label>DNI</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ingrese DNI"
                      value={dniCliente}
                      onChange={(event) => setDniCliente(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="formTelefonoCliente">
                    <Form.Label>Teléfono</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ingrese teléfono"
                      value={telefono}
                      onChange={(event) => setTelefono(event.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            Agregar
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default AddEventoModal
